import React, { useEffect, useState } from 'react';
import { Loader2, Calendar, ExternalLink, Plus } from 'lucide-react';
import { api } from './api';
import { GhlBadge } from './ContactTasks';
import { fromApi } from './kit/time';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import { useAuth } from './AuthContext';
import BookDialog, { confirmGhlRefused, errorDetail } from './calendar/BookDialog';

const when = (iso) => iso ? fromApi(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const newestFirst = (list) => [...list].sort((x, y) => fromApi(y.start_at) - fromApi(x.start_at));

// Contact profile → Appointments tab (ticket 135 S3): GHL calendar events
// mirrored, newest first. Cancelled rows stay, shown struck. Ticket 188: book
// and reschedule from here through the Calendar's own write path (GHL-first,
// then the mirror); title, status and the rest still change in GHL.
export default function ContactAppointments({ contactId, contactName, ghlManaged = false, ghlEditUrl }) {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [dialog, setDialog] = useState(null); // { mode: 'create' } | { mode: 'reschedule', appointment }
  const [moving, setMoving] = useState(null); // appointment_id mid-reschedule
  const fixedContact = { contact_id: contactId, display_name: contactName };

  useEffect(() => {
    let alive = true;
    api.get(`/v2/contacts/${contactId}/appointments`)
      .then(({ data }) => { if (alive) setRows(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [contactId]);

  const placeRow = (row) => setRows((cur) => newestFirst([...(cur || []).filter((r) => r.appointment_id !== row.appointment_id), row]));

  // The dialog has already asked about availability (`override` is its answer).
  // GHL is called first; the row only changes on success. Never throws.
  const reschedule = async (a, body, override) => {
    setMoving(a.appointment_id);
    try {
      const send = (ov) => api.patch(`/v2/appointments/${a.appointment_id}/time`, { ...body, override: ov });
      let res;
      try {
        res = await send(override);
      } catch (e) {
        const { message, code } = errorDetail(e);
        if (override || code !== 'ghl_rejected') throw e;
        if (!(await confirmGhlRefused(message, 'Move anyway'))) {
          toast({ title: 'Not rescheduled', description: message });
          return;
        }
        res = await send(true);
      }
      placeRow({ ...a, ...res.data });
      toast({ title: 'Rescheduled' });
    } catch (e) {
      toast({ title: 'Not rescheduled', description: errorMessage(e, 'GoHighLevel did not accept the change.') });
    } finally {
      setMoving(null);
    }
  };

  const bookButton = ghlManaged && (
    <button className="btn-gold" onClick={() => setDialog({ mode: 'create' })} data-testid="contact-appts-book">
      <Plus size={14} /> Book appointment
    </button>
  );
  const bookDialog = dialog && (
    <BookDialog
      open
      mode={dialog.mode}
      appointment={dialog.appointment}
      fixedContact={fixedContact}
      canViewAll={(user?.permissions || []).includes('loans.view_all')}
      me={user?.user_id}
      onClose={() => setDialog(null)}
      onSaved={(row) => { placeRow(row); toast({ title: 'Appointment booked' }); }}
      onReschedule={reschedule}
    />
  );

  if (rows === null) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="contact-appts-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading appointments...
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="contact-appts-empty">
        <p className="text-sm">No appointments for this contact.</p>
        <p className="text-xs mt-1">Appointments booked in GoHighLevel show up here.</p>
        {bookButton && <div className="mt-4 flex justify-center">{bookButton}</div>}
        {bookDialog}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {bookButton && <div className="flex justify-end">{bookButton}</div>}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="contact-appts-list">
      {rows.map(a => {
        const cancelled = (a.status || '').toLowerCase() === 'cancelled';
        return (
          <div key={a.appointment_id} className="flex items-start gap-3 px-4 py-3" data-testid={`contact-appt-${a.appointment_id}`}>
            <Calendar size={14} className="text-slate-400 mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium truncate ${cancelled ? 'line-through text-slate-400' : 'text-brand-navy'}`}
                  data-testid={cancelled ? `contact-appt-cancelled-${a.appointment_id}` : undefined}>
                  {a.title}
                </span>
                {a.source === 'ghl' && <GhlBadge testId={`ghl-badge-${a.appointment_id}`} />}
                {a.status && <span className="text-[10px] uppercase font-semibold text-slate-500">{a.status}</span>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {[when(a.start_at), a.calendar_name, a.owner_name].filter(Boolean).join(' · ')}
              </div>
            </div>
            {a.source === 'ghl' && !cancelled && (
              <button type="button" disabled={moving === a.appointment_id}
                onClick={() => setDialog({ mode: 'reschedule', appointment: { ...a, contact_name: contactName } })}
                className="text-xs inline-flex items-center gap-1 text-indigo-700 underline flex-shrink-0 disabled:opacity-50" data-testid={`contact-appt-reschedule-${a.appointment_id}`}>
                {moving === a.appointment_id && <Loader2 className="animate-spin" size={11} />} Reschedule
              </button>
            )}
            {a.source === 'ghl' && ghlEditUrl && (
              <a href={ghlEditUrl} target="_blank" rel="noopener noreferrer"
                className="text-xs inline-flex items-center gap-0.5 text-indigo-700 underline flex-shrink-0" data-testid={`contact-appt-open-ghl-${a.appointment_id}`}>
                Open in GHL <ExternalLink size={11} />
              </a>
            )}
          </div>
        );
      })}
      </div>
      {bookDialog}
    </div>
  );
}
